import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import svcs from '../../../services';
import { parseSanitize } from '../../../util/commonUtils';
import { useLoginInfo } from '../../../hooks/useLoginInfo';
import { RegulationFileReq, RegulationListRes } from 'communitypage/regulation/types/regulation.types';
import { useRegulationApi } from 'communitypage/regulation/hooks/useApiRegulationPage';
import { EditorStyle } from 'style/SsqEditorStyle';
import { SsqImages } from 'images/SsqImages';
import SsqEditor5 from 'libraries/SsqEditor5';
import { VersionWrapper, DownloadDocItem } from 'communitypage/privacypolicy/page/PrivacyPolicyPage';
import { langutil } from 'language/langutil';
import downarrow from '../../../mainpage/knowledgebase/img/down-arrow.svg';
import { BoxRadioButton, VersionFileAttachArea } from 'borispage/common/AdminCommonComponents';
import { IFile } from 'repositories/SsqDataRepository';
import { fontFamilyBold, fontFamilyRegular } from 'style/fonts';
import { BRKS } from 'util/variables';

interface EditForm {
  title: string;
  description: string;
  files: RegulationFileReq[];
}

const CraPage: React.FC = () => {
  const loginInfo = useLoginInfo();
  const isSystemAdmin = loginInfo?.allowAdminManage;

  const { country, lang: urlLang } = langutil.splitLangCode(svcs.svcLang.lang());
  const exposeSiteLanguage = langutil.getLangCode(country, urlLang);

  const [regulationList, setRegulationList] = useState<RegulationListRes[]>([]);
  const [selectedItem, setSelectedItem] = useState<RegulationListRes | null>(null);
  const [isDocDropdownOpen, setIsDocDropdownOpen] = useState(false);
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditForm>({ title: '', description: '', files: [] });
  const [editorKey, setEditorKey] = useState(0);

  const [isNewMode, setIsNewMode] = useState(false);
  const [newForm, setNewForm] = useState<EditForm>({ title: '', description: '', files: [] });
  const [newEditorKey, setNewEditorKey] = useState(0);


  const { getRegulationList, createRegulation, updateRegulation, deleteRegulation, uploadRegulationFiles } = useRegulationApi();

  const isEditing = editingId !== null;

  const trans = (key: string) => {
    svcs.svcLang.setNamespace('I18N_NAMESPACE_ADMIN_PAGE');
    return svcs.svcLang.getTranslationByKey(key) || '';
  };

  useEffect(() => {
    if (!isDocDropdownOpen && !isMoreMenuOpen) return;
    const close = () => {
      setIsDocDropdownOpen(false);
      setIsMoreMenuOpen(false);
    };
    document.addEventListener('click', close);
    return () => document.removeEventListener('click', close);
  }, [isDocDropdownOpen, isMoreMenuOpen]);

  useEffect(() => {
    if (!loginInfo) return;
    fetchList();
  }, [loginInfo]);

  // CRA 규제 목록 조회. 저장 후 특정 항목을 유지하려면 preferItemId 전달
  const fetchList = async (preferItemId?: number) => {
    try {
      const data = await getRegulationList('CRA', exposeSiteLanguage);
      setRegulationList(data);
      if (preferItemId) {
        const found = data.find((d) => d.id === preferItemId);
        setSelectedItem(found ?? (data.length > 0 ? data[0] : null));
      } else {
        setSelectedItem(data.length > 0 ? data[0] : null);
      }
      setEditingId(null);
    } catch (e) {
      console.error('규제 목록 조회 실패', e);
    }
  };

  const handleToggleDocDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (regulationList.length <= 1) return;
    setIsDocDropdownOpen((prev) => !prev);
    setIsMoreMenuOpen(false);
  };

  // 수정 폼: Blob 업로드 후 기존 파일 목록에 추가
  const handleUploadEditFiles = async (files: FileList) => {
    if (!files || files.length === 0) return;
    try {
      const uploaded = await uploadRegulationFiles(Array.from(files));
      setEditForm((f) => ({ ...f, files: [...f.files, ...uploaded] }));
    } catch (e) {
      console.error('파일 업로드 실패', e);
    }
  };

  // 신규 폼: Blob 업로드 후 파일 목록에 추가
  const handleUploadNewFiles = async (files: FileList) => {
    if (!files || files.length === 0) return;
    try {
      const uploaded = await uploadRegulationFiles(Array.from(files));
      setNewForm((f) => ({ ...f, files: [...f.files, ...uploaded] }));
    } catch (e) {
      console.error('파일 업로드 실패', e);
    }
  };

  // 수정 폼: fileKey 기준으로 파일 제거 (Blob 실제 삭제는 저장 시 서버에서 처리)
  const handleRemoveEditFile = (file: IFile) => {
    setEditForm((f) => ({ ...f, files: f.files.filter((item) => item.fileKey !== file.fileKey) }));
  };

  // 신규 폼: fileKey 기준으로 파일 제거
  const handleRemoveNewFile = (file: IFile) => {
    setNewForm((f) => ({ ...f, files: f.files.filter((item) => item.fileKey !== file.fileKey) }));
  };

  const mapToIFile = (files: RegulationFileReq[]): IFile[] =>
    files.map((f) => ({
      fileName: f.fileName,
      fileKey: f.fileKey,
      fileSize: f.fileSize,
      blobUrlForLargeFile: f.blobUrlForLargeFile || '',
      id: 0,
      fileLang: 'EN',
      fileType: 'NONE',
      targetSoftwareType: 'NONE',
      downloadCount: 0,
      writerOnly: false,
      searchExpose: false,
    }));

  // 수정 모드 진입
  const handleClickEdit = (item: RegulationListRes) => {
    setEditingId(item.id);
    setEditForm({ title: item.title, description: item.description, files: item.files });
    setEditorKey((k) => k + 1);
    setIsNewMode(false);
  };

  // 수정 취소
  const handleCancelEdit = () => {
    setEditingId(null);
  };

  // 수정 저장
  const handleSaveEdit = async (item: RegulationListRes) => {
    if (!editForm.title.trim()) {
      window.alert(trans('I18N_PPWR_PLACEHOLDER_TITLE'));
      return;
    }
    if (!editForm.description.trim() || editForm.description === '<p>&nbsp;</p>' || editForm.description === '<p></p>') {
      window.alert(trans('I18N_EMPTY_ALERT'));
      return;
    }
    try {
      await updateRegulation(item.id, {
        title: editForm.title,
        description: editForm.description,
        exposeSiteLanguage: item.exposeSiteLanguage,
        category: item.category,
        files: editForm.files,
      });
      await fetchList(item.id);
    } catch (e) {
      console.error('규제 수정 실패', e);
      window.alert(trans('I18N_PLACEHOLDER_SAVED_FAIL'));
    }
  };

  const handleDelete = async (id: number) => {
    const confirmed = window.confirm(trans('I18N_ADMIN_CONFIRM_DELETE'));
    if (!confirmed) return;
    try {
      await deleteRegulation(id);
      await fetchList();
    } catch (e) {
      console.error('규제 삭제 실패', e);
    }
  };

  const handleClickAdd = () => {
    setIsNewMode(true);
    setNewForm({ title: '', description: '', files: [] });
    setNewEditorKey((k) => k + 1);
    setEditingId(null);
  };

  const handleCancelNew = () => {
    setIsNewMode(false);
  };

  const handleSaveNew = async () => {
    if (!newForm.title.trim()) {
      window.alert(trans('I18N_PPWR_PLACEHOLDER_TITLE'));
      return;
    }
    if (!newForm.description.trim() || newForm.description === '<p>&nbsp;</p>' || newForm.description === '<p></p>') {
      window.alert(trans('I18N_EMPTY_ALERT'));
      return;
    }
    try {
      await createRegulation({
        title: newForm.title,
        description: newForm.description,
        exposeSiteLanguage: exposeSiteLanguage,
        category: 'CRA',
        files: newForm.files,
      });
      setIsNewMode(false);
      await fetchList();
    } catch (e) {
      console.error('규제 생성 실패', e);
      window.alert(trans('I18N_PLACEHOLDER_SAVED_FAIL'));
    }
  };

  return (
    <Container>
      <div className="page_title">{trans('I18N_CRA_PAGE_TITLE')}</div>
      <BannerImage>
        <div className="banner_text">
          <div className="banner_title">{trans('I18N_CRA_PAGE_TITLE')}</div>
          <div className="banner_desc">
            <span className="light">{trans('I18N_CRA_BANNER_DESC_LIGHT')}</span>
            <span className="bold">{trans('I18N_CRA_BANNER_DESC_BOLD')}</span>
          </div>
        </div>
      </BannerImage>

      <CategoryTabs>
        {isSystemAdmin && (isEditing || isNewMode) && (
          <div className="right_btns">
            {isEditing ? (
              <>
                <button className="cancel_pill_btn" onClick={handleCancelEdit}>
                  {trans('I18N_NOTI_BTN_CANCEL')}
                </button>
                <button
                  className="save_pill_btn"
                  onClick={() => {
                    if (selectedItem) handleSaveEdit(selectedItem);
                  }}
                >
                  {trans('I18N_NOTI_BTN_SAVE')}
                </button>
              </>
            ) : (
              <>
                <button className="cancel_pill_btn" onClick={handleCancelNew}>
                  {trans('I18N_NOTI_BTN_CANCEL')}
                </button>
                <button className="save_pill_btn" onClick={handleSaveNew}>
                  {trans('I18N_NOTI_BTN_SAVE')}
                </button>
              </>
            )}
          </div>
        )}
      </CategoryTabs>

      <RegulationList>
        {isNewMode && (
          <NewItemForm>
            <input
              className="title_input"
              placeholder={trans('I18N_PPWR_PLACEHOLDER_TITLE')}
              value={newForm.title}
              onChange={(e) => setNewForm((f) => ({ ...f, title: e.target.value }))}
            />
            <EditorArea>
              <SsqEditor5
                key={newEditorKey}
                contents=""
                width="100%"
                height="480px"
                lang={svcs.svcLang.lang()}
                onChangeData={(data: string) => setNewForm((f) => ({ ...f, description: data }))}
              />
            </EditorArea>
            <HiddenRadioWrapper>
              <VersionFileAttachArea
                files={mapToIFile(newForm.files)}
                versionIndex={0}
                onDeleteFile={handleRemoveNewFile}
                onUploadFile={handleUploadNewFiles}
                setFileLang={() => {}}
                setFileType={() => {}}
                setTargetSoftwareType={() => {}}
                readonly={false}
              />
            </HiddenRadioWrapper>
          </NewItemForm>
        )}

        {!isNewMode && regulationList.length === 0 && (
          <>
            {isSystemAdmin && (
              <EmptyAddRow>
                <AddBtn onClick={handleClickAdd}>+ {trans('I18N_ADD_BTN')}</AddBtn>
              </EmptyAddRow>
            )}
            <div className="empty_msg">{trans('I18N_PPWR_EMPTY_LIST')}</div>
          </>
        )}

        {!isNewMode && selectedItem && (
          <DocSection>
            {isEditing ? (
              <TitleInputInline
                value={editForm.title}
                onChange={(e) => setEditForm((f) => ({ ...f, title: e.target.value }))}
              />
            ) : (
              <>
                <DocHeaderRow>
                  <DocVersionWrapper>
                    <div className="version">Doc No.</div>

                    <DocBtnWrapper>
                      <DocBtn
                        onClick={handleToggleDocDropdown}
                        style={{ cursor: regulationList.length > 1 ? 'pointer' : 'default' }}
                      >
                        {selectedItem.title}
                        {regulationList.length > 1 && <img src={downarrow} />}
                      </DocBtn>

                      {isDocDropdownOpen && regulationList.length > 1 && (
                        <div className="version_dropdown">
                          {regulationList.map((item) => (
                            <div
                              key={item.id}
                              className="version_dropdown_item"
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedItem(item);
                                setIsDocDropdownOpen(false);
                              }}
                            >
                              <span className="version_text">{item.title}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </DocBtnWrapper>

                    {isSystemAdmin && (
                      <div
                        className="btn-list-modify"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsMoreMenuOpen((prev) => !prev);
                          setIsDocDropdownOpen(false);
                        }}
                      >
                        {isMoreMenuOpen && (
                          <div className="view_more_dropdown">
                            <button
                              className="view_more_dropdown_item modify"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsMoreMenuOpen(false);
                                handleClickEdit(selectedItem);
                              }}
                            >
                              {trans('I18N_MODIFIED')}
                            </button>
                            <button
                              className="view_more_dropdown_item delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                setIsMoreMenuOpen(false);
                                handleDelete(selectedItem.id);
                              }}
                            >
                              {trans('I18N_BUTTON_DELETE')}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </DocVersionWrapper>

                  {isSystemAdmin && (
                    <AddBtn onClick={handleClickAdd}>+ {trans('I18N_ADD_BTN')}</AddBtn>
                  )}
                </DocHeaderRow>

              </>
            )}

            {isEditing ? (
              <>
                <EditorArea>
                  <SsqEditor5
                    key={editorKey}
                    contents={editForm.description}
                    width="100%"
                    height="480px"
                    lang={svcs.svcLang.lang()}
                    onChangeData={(data: string) => setEditForm((f) => ({ ...f, description: data }))}
                  />
                </EditorArea>
                <HiddenRadioWrapper>
                  <VersionFileAttachArea
                    files={mapToIFile(editForm.files)}
                    versionIndex={1}
                    onDeleteFile={handleRemoveEditFile}
                    onUploadFile={handleUploadEditFiles}
                    setFileLang={() => {}}
                    setFileType={() => {}}
                    setTargetSoftwareType={() => {}}
                    readonly={false}
                  />
                </HiddenRadioWrapper>
              </>
            ) : (
              <>
                {selectedItem.files && selectedItem.files.length > 0 && (
                  <FileDownloadSection>
                    {selectedItem.files.map((file) => (
                      <a
                        key={file.id}
                        className="download_doc_item"
                        href={
                          file.blobUrlForLargeFile
                            ? file.blobUrlForLargeFile
                            : `/uploads/${file.fileKey}/${encodeURIComponent(file.fileName)}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <DownloadDocItem>
                          <span className="file_name">{file.fileName}</span>
                          <img alt="download_icon" src={SsqImages.common.icon.downloadImg()} />
                          <span className="file_size">{formatFileSize(file.fileSize)}</span>
                        </DownloadDocItem>
                      </a>
                    ))}
                  </FileDownloadSection>
                )}
                <DocContent>
                  <div className="SsqEditor ck-content">
                    {parseSanitize(selectedItem.description)}
                  </div>
                </DocContent>
              </>
            )}
          </DocSection>
        )}
      </RegulationList>
    </Container>
  );
};

const formatFileSize = (size: number) => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

export default CraPage;

const Container = styled.div`
  max-width: 120rem;
  margin: 0 auto;
  height: fit-content;
  padding-bottom: 88px;

  @media (max-width: 1200px) {
    padding: 0 18px 88px;
  }

  .page_title {
    height: 80px;
    margin-top: 50px;
    font-size: 35px;
    font-weight: bold;

    @media (max-width: ${BRKS.mobileMax}) {
      font-size: 25px;
    }

    @media (max-width: ${BRKS.miniMax}) {
      font-size: 20px;
    }
  }
`;

const BannerImage = styled.div`
  background-image: url(${SsqImages.community.craBanner()});
  position: relative;
  width: 100%;
  height: 320px;
  margin-bottom: 1rem;
  border-radius: 8px;
  background-color: #e8edf5;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  display: flex;
  align-items: center;

  .banner_text {
    padding-left: 6rem;

    @media (max-width: ${BRKS.mobileMax}) {
      padding-left: 2.4rem;
      padding-right: 1rem;
    }
  }

  .banner_title {
    font-size: 2.8rem;
    font-weight: bold;
    color: #fff;
    text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
    line-height: 1.3;
    
      @media (max-width: ${BRKS.mobileMax}) {
      display: none;
    }
  }

  .banner_desc {
    font-size: 1.4rem;
    color: rgba(255, 255, 255, 0.9);
    margin-top: 1rem;
    text-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
    line-height: 1.6;
    max-width: 80rem;

    .light {
      ${fontFamilyRegular};
      color: rgba(255, 255, 255, 0.8);
      display: block;
    }

    .bold {
      ${fontFamilyBold};
      display: block;
    }

    @media (max-width: ${BRKS.mobileMax}) {
      font-size: 1.3rem;
    }
  }

  @media (max-width: ${BRKS.mobileMax}) {
    height: 150px;
    margin-bottom: 2.4rem;
  }
`;

const CategoryTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  margin-bottom: 1.4rem;
  flex-wrap: wrap;

  .tab_btn {
    padding: 0.6rem 2rem;
    border: 2px solid #e5e5e5;
    border-radius: 5rem;
    font-size: 1.4rem;
    background: #fff;
    cursor: pointer;
    color: #191a1c;
    transition: all 0.15s;

    &.active {
      border-color: #1a6ce8;
      color: #1a6ce8;
      font-weight: 600;
    }

    &:hover:not(.active) {
      border-color: #999;
    }

    @media (max-width: ${BRKS.mobileMax}) {
      font-size: 1.2rem;
      padding: 0.5rem 1.4rem;
    }
  }

  .right_btns {
    margin-left: auto;
    display: flex;
    gap: 0.6rem;
  }

  .cancel_pill_btn,
  .save_pill_btn {
    padding: 0.6rem 1.6rem;
    border-radius: 5rem;
    font-size: 1.4rem;
    cursor: pointer;

    @media (max-width: ${BRKS.mobileMax}) {
      font-size: 1.2rem;
      padding: 0.5rem 1.2rem;
    }
  }

  .save_pill_btn {
    border: 2px solid #1a6ce8;
    background: #1a6ce8;
    color: #fff;

    &:hover {
      background: #155bbf;
      border-color: #155bbf;
    }
  }

  .cancel_pill_btn {
    border: 2px solid #bec8db;
    background: #fff;
    color: #4b5563;

    &:hover {
      background: #f3f4f6;
    }
  }
`;

const RegulationList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  .empty_msg {
    padding: 4rem 0;
    text-align: center;
    font-size: 1.4rem;
    color: #888;
  }
`;

const NewItemForm = styled.div`
  border-top: 1px solid #e5e5e5;
  border-bottom: 1px solid #e5e5e5;
  padding: 1.6rem 0;

  .title_input {
    width: 100%;
    font-size: 1.6rem;
    font-weight: 500;
    border: 1px solid rgb(199, 214, 234);
    border-radius: 4px;
    padding: 0.6rem 1rem;
    margin-bottom: 1.2rem;
    outline: none;
    box-sizing: border-box;

    &:focus {
      border-color: #1a6ce8;
    }
  }
`;

const HiddenRadioWrapper = styled.div`
  margin-top: 1.2rem;

  ${BoxRadioButton} {
    display: none !important;
  }

  .remove_btn {
    background: none;
    border: none;
    cursor: pointer;
    flex-shrink: 0;
  }

  .file-name {
    all: unset;
    font-size: 1.2rem;
    background: rgba(199, 214, 234, 0.5);
    border: 1px solid rgb(199, 214, 234);
    border-radius: 4px;
    width: 32.8rem;
    max-width: calc(100vw - 12rem);
    height: 3.2rem;
    line-height: 3.2rem;
    color: rgb(64, 125, 217);
    font-family: 'Pretendard Bold', sans-serif;
    display: inline-block;
    padding: 0px 1.2rem;
    vertical-align: middle;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    cursor: pointer;
    box-sizing: border-box;
  }

  .file-size {
    font-size: 1.2rem;
    color: #9ca3af;
    white-space: nowrap;

    @media (max-width: ${BRKS.mobileMax}) {
      display: none;
    }
  }

  .file-item {
    display: flex;
    align-items: center;
    gap: 0.8rem;
    margin-bottom: 0.8rem;
  }

  .file_add_btn {
    color: #407edb;
    ${fontFamilyBold};
    font-size: 1.2rem;
    line-height: 16px;

    img {
      margin-right: 0.8rem;
      vertical-align: top;
    }
  }
`;

const FileDownloadSection = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;

  .download_doc_item {
    display: flex;
    max-width: 60rem;
    align-items: center;
    font-size: 16px;
    font-weight: normal;
    height: 40px;
    color: #3f7dd9;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }

    @media (max-width: ${BRKS.mobileMax}) {
      font-size: 1.4rem;
      max-width: 100%;
      height: auto;
      padding: 0.4rem 0;
    }
  }
`;

const DocVersionWrapper = styled(VersionWrapper)`
  .version_dropdown_item {
    font-size: 15px;
    padding: 15px 30px;
  }

  .btn-list-modify {
    position: relative;
  }

  .view_more_dropdown {
    top: 100%;
    left: 0;
  }
`;

const DocBtnWrapper = styled.div`
  position: relative;
  display: inline-block;

  .version_dropdown {
    left: auto;
    right: 0;
    width: 100%;
    min-width: unset;
  }
`;

const DocSection = styled.div`
  border-top: 1px solid #e5e5e5;
  padding-top: 2.4rem;
`;

const DocHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2rem;
`;

const DocBtn = styled.div`
  border: 2px solid #e5e5e5;
  box-shadow: rgba(0, 0, 0, 0.1) 0px 4px 20px;
  color: #191a1c;
  border-radius: 5rem;
  padding: 0.6rem 2rem;

  display: flex;
  align-items: center;
  max-width: 40rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 40px;
  font-size: 15px;

  & img {
    margin-left: 1.5rem;
    flex-shrink: 0;
  }

  @media (max-width: ${BRKS.mobileMax}) {
    font-size: 1.3rem;
    padding: 0.5rem 1.4rem;
    max-width: calc(100vw - 10rem);
  }
`;

const TitleInputInline = styled.input`
  width: 100%;
  box-sizing: border-box;
  font-size: 2rem;
  font-weight: 400;
  color: #191a1c;
  border: 1px solid rgb(199, 214, 234);
  border-radius: 4px;
  padding: 0.6rem 1rem;
  margin-bottom: 1.2rem;
  outline: none;

  &:focus {
    border-color: #1a6ce8;
  }

  @media (max-width: ${BRKS.mobileMax}) {
    font-size: 1.6rem;
  }
`;

const AddBtn = styled.button`
  padding: 0.6rem 1.6rem;
  border-radius: 5rem;
  font-size: 1.4rem;
  cursor: pointer;
  border: 2px solid #1a6ce8;
  background: #1a6ce8;
  color: #fff;
  white-space: nowrap;
  flex-shrink: 0;

  &:hover {
    background: #155bbf;
    border-color: #155bbf;
  }

  @media (max-width: ${BRKS.mobileMax}) {
    font-size: 1.2rem;
    padding: 0.5rem 1.2rem;
  }
`;

const EmptyAddRow = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-top: 2.4rem;
  padding-bottom: 1.6rem;
  border-top: 1px solid #e5e5e5;
`;

const EditorArea = styled.div`
  padding: 1.2rem 0;

  .ck.ck-editor__editable.ck-editor__editable_inline {
    overflow-y: auto;
    height: 480px;
    border: 1px solid rgb(199, 214, 234) !important;
    border-top: none !important;

    @media (max-width: ${BRKS.mobileMax}) {
      height: 320px;
    }
  }
`;

const DocContent = styled.div`
  ${EditorStyle};
  padding: 2rem 0 2.4rem;
  overflow-x: auto;

  .ck-content .table,
  .SsqEditor figure:has(table) {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    max-width: 100%;
  }

  .ck-content table,
  .SsqEditor table {
    display: table !important;
    min-width: 600px;
  }

  .ck-content table td,
  .ck-content table th,
  .SsqEditor table td,
  .SsqEditor table th {
    word-break: keep-all;
    font-size: 13px;
  }
`;
